import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useMemberHistory, type AffectationsParAnnee, type AffectationHistorique } from '@/hooks/useMemberHistory';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  Coins,
  Building2,
  History,
  AlertCircle,
  Download,
  Loader2,
} from 'lucide-react';
import { HistoriqueMemberPDF } from '@/components/pdf/HistoriqueMemberPDF';
import { useToast } from '@/hooks/use-toast';

interface MemberHistoryTabProps {
  membreId: string;
  tauxHoraire: number;
  coefficientCharges: number;
  heuresParJour: number;
  // Infos membre pour le PDF
  prenom: string;
  nom: string;
  poste: string;
  specialite: string;
}

/**
 * Onglet Historique des affectations d'un membre
 * Groupé par année, avec pagination progressive
 */
export function MemberHistoryTab({
  membreId,
  tauxHoraire,
  coefficientCharges,
  heuresParJour,
  prenom,
  nom,
  poste,
  specialite,
}: MemberHistoryTabProps) {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    affectationsParAnnee,
    isLoading,
    error,
    totalJoursOuvres,
    totalCoutMO,
    nombreChantiers,
    visibleCount,
    hasMore,
    showMore,
    currentPage,
    totalPages,
    goToPage,
  } = useMemberHistory({ membreId, tauxHoraire, coefficientCharges });

  // État pour les années dépliées (toutes ouvertes par défaut)
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());

  // Toggle une année
  const toggleYear = (year: number) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  // Initialiser les années dépliées quand les données changent
  React.useEffect(() => {
    if (affectationsParAnnee.length > 0) {
      // Ouvrir la première année par défaut
      setExpandedYears(new Set([affectationsParAnnee[0].annee]));
    }
  }, [affectationsParAnnee.length]);

  // Télécharger le PDF de l'historique
  const handleDownloadPDF = async () => {
    if (affectationsParAnnee.length === 0) {
      toast({
        title: 'Aucun historique',
        description: 'Ce membre n\'a pas encore d\'affectation à exporter.',
        variant: 'destructive',
      });
      return;
    }

    setIsDownloading(true);
    try {
      const blob = await pdf(
        <HistoriqueMemberPDF
          membre={{
            prenom,
            nom,
            poste,
            specialite,
            taux_horaire: tauxHoraire,
            coefficient_charges: coefficientCharges,
            heures_par_jour: heuresParJour,
          }}
          affectationsParAnnee={affectationsParAnnee}
          totalJoursOuvres={totalJoursOuvres}
          totalCoutMO={totalCoutMO}
          nombreChantiers={nombreChantiers}
        />
      ).toBlob();

      // Créer le lien de téléchargement
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `historique-${prenom.toLowerCase()}-${nom.toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF téléchargé',
        description: `Historique de ${prenom} ${nom} exporté avec succès.`,
      });
    } catch (error) {
      console.error('[MemberHistoryTab] Erreur PDF:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de générer le PDF. Réessayez.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Loading state
  if (isLoading && affectationsParAnnee.length === 0) {
    return (
      <div className="space-y-4 p-1">
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
        <Skeleton className="h-12" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-destructive font-medium">Erreur de chargement</p>
        <p className="text-muted-foreground text-sm mt-1">{error}</p>
      </div>
    );
  }

  // Empty state
  if (affectationsParAnnee.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted/50 p-4 mb-4">
          <History className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="font-medium text-foreground">Aucun historique</p>
        <p className="text-muted-foreground text-sm mt-1">
          Ce membre n'a pas encore été affecté à un chantier
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-1">
      {/* Header avec bouton export */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadPDF}
          disabled={isDownloading || affectationsParAnnee.length === 0}
          className="gap-2"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Exporter PDF
        </Button>
      </div>

      {/* Statistiques globales */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs mb-1">
              <Clock className="h-3 w-3" />
              Jours travaillés
            </div>
            <div className="font-bold text-lg font-mono">{totalJoursOuvres.toFixed(1)}</div>
          </CardContent>
        </Card>

        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs mb-1">
              <Coins className="h-3 w-3" />
              Coût total
            </div>
            <div className="font-bold text-lg font-mono text-sky-400">
              {totalCoutMO.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
          </CardContent>
        </Card>

        <Card className="bg-muted/30 border-muted">
          <CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs mb-1">
              <Building2 className="h-3 w-3" />
              Chantiers
            </div>
            <div className="font-bold text-lg font-mono">{nombreChantiers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Liste par année */}
      <ScrollArea className="h-[350px] pr-2">
        <div className="space-y-2">
          {affectationsParAnnee.map((groupe) => (
            <YearGroup
              key={groupe.annee}
              groupe={groupe}
              isExpanded={expandedYears.has(groupe.annee)}
              onToggle={() => toggleYear(groupe.annee)}
            />
          ))}
        </div>

        {/* Bouton "Voir plus" */}
        {hasMore && (
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={showMore}>
              Voir plus ({visibleCount} affichées)
            </Button>
          </div>
        )}

        {/* Pagination (après 50) */}
        {totalPages > 1 && visibleCount >= 50 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// Composant pour un groupe d'année
interface YearGroupProps {
  groupe: AffectationsParAnnee;
  isExpanded: boolean;
  onToggle: () => void;
}

function YearGroup({ groupe, isExpanded, onToggle }: YearGroupProps) {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg cursor-pointer hover:bg-muted/60 transition-colors">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-bold">{groupe.annee}</span>
            <Badge variant="secondary" className="text-xs">
              {groupe.affectations.length} chantier{groupe.affectations.length > 1 ? 's' : ''}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">
              {groupe.totalJours.toFixed(1)} j
            </span>
            <span className="font-mono font-medium text-sky-400">
              {groupe.totalCout.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </span>
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="pl-6 pt-2 space-y-2">
          {groupe.affectations.map((aff) => (
            <AffectationCard key={aff.id} affectation={aff} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Composant pour une affectation
interface AffectationCardProps {
  affectation: AffectationHistorique;
}

function AffectationCard({ affectation }: AffectationCardProps) {
  const isRetard = affectation.notes?.includes('Retard') ?? false;
  const isReprise = affectation.notes?.includes('Reprise') ?? false;
  const isDifferee = affectation.notes?.includes('Différée') ?? false;

  // Formater les dates
  const dateDebut = new Date(affectation.date_debut);
  const dateFin = new Date(affectation.date_fin);
  const isSameDay = affectation.date_debut === affectation.date_fin;

  const formatDate = (date: Date) =>
    date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

  return (
    <Card className="bg-background border-muted/50">
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Nom du chantier */}
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: affectation.chantier.couleur || '#6b7280' }}
              />
              <span className="font-medium text-sm">
                {affectation.chantier.nom_chantier}
              </span>
              {/* Badges spéciaux */}
              {isRetard && (
                <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/30">
                  Retard
                </Badge>
              )}
              {isReprise && (
                <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/30">
                  Reprise
                </Badge>
              )}
              {isDifferee && (
                <Badge variant="outline" className="text-xs text-blue-400 border-blue-400/30">
                  Différée
                </Badge>
              )}
            </div>

            {/* Dates et heures */}
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {isSameDay ? (
                <span>{formatDate(dateDebut)}</span>
              ) : (
                <span>
                  {formatDate(dateDebut)} → {formatDate(dateFin)}
                </span>
              )}
              <span className="text-muted-foreground/50">•</span>
              <span>{affectation.jours_travailles} j</span>
              {affectation.heures_par_jour && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <span>{affectation.heures_par_jour}h/j</span>
                </>
              )}
              {/* Total heures */}
              {affectation.total_heures && affectation.total_heures > 0 && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <span className="font-medium text-foreground">{affectation.total_heures}h</span>
                </>
              )}
            </div>

            {/* Heures supplémentaires */}
            {affectation.heures_sup && affectation.heures_sup.total_heures > 0 && (
              <div className="flex items-center gap-2 mt-1 text-xs">
                <Clock className="h-3 w-3 text-amber-400" />
                <span className="text-amber-400 font-medium">
                  +{affectation.heures_sup.total_heures}h sup
                </span>
                <span className="text-muted-foreground">
                  ({affectation.heures_sup.total_cout.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €)
                </span>
              </div>
            )}
          </div>

          {/* Coût */}
          <div className="text-right">
            <div className="font-mono font-medium text-sm">
              {(affectation.cout_mo ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
            {/* Si heures sup, afficher coût total */}
            {affectation.heures_sup && affectation.heures_sup.total_cout > 0 && (
              <div className="text-xs text-muted-foreground">
                + {affectation.heures_sup.total_cout.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € HS
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
